import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import TranslatedText from '@/components/TranslatedText';
import { makeImageSourceSet } from '@/utils/imageUtilities';
import isBrowser from '@/utils/isBrowser';
import { SF_URL } from '@/constants/env';
import ProductSummaryMediaContainer from './ProductSummaryMedia.styles';
import { ProductSummaryMediaProps } from './ProductSummaryMedia.d';

const loadingPicture = (
  <div data-loading="true" className="product-summary__media-image">
    <div />
  </div>
);

export default function ProductSummaryMedia(
  props: ProductSummaryMediaProps
): JSX.Element {
  const { primary_image, purchasable, promotional_badge } = props;

  const srcSetArr = makeImageSourceSet(primary_image.path, {
    aspectRatio: 'squared',
    fit: 'cover',
    bpConfig: {
      SM: {
        quality: 40,
        size: 424,
      },
      MD: {
        quality: 40,
        size: 688,
      },
      LG: {
        quality: 80,
        size: 908,
      },
      XL: {
        size: 832,
      },
    },
  });

  const srcSetBadges = makeImageSourceSet(promotional_badge?.path, {
    fit: 'scale-down',
    bpConfig: {
      SM: {
        quality: 40,
        size: 224,
      },
      MD: {
        quality: 40,
        size: 352,
      },
      LG: {
        quality: 80,
        size: 384,
      },
    },
  });

  const srcSet = srcSetArr.join();

  const pictureNode = (
    <LazyLoadImage
      effect="blur"
      wrapperClassName="product-summary__media-image"
      src={`${SF_URL}${primary_image.path}`}
      srcSet={srcSet}
      alt={primary_image.alt_text}
      width="100%"
      height="100%"
      sizes="(max-width: 479px) 212px,
        (min-width: 480px and max-width: 767px) 344px,
        (min-width: 768px and max-width: 991px) 454px,
        (min-width: 992px) 416px"
    />
  );

  return (
    <ProductSummaryMediaContainer className="product-summary__media">
      {isBrowser() ? pictureNode : loadingPicture}

      {/*TODO: move text strings to single translation (RUI-28) */}
      {!purchasable && (
        <TranslatedText
          fontSize="sm"
          textTransform="uppercase"
          color="white"
          fontWeight="600"
          letterSpacing="widest"
          textAlign="center"
          backgroundColor={['black50', 'black30']}
          className="product-summary__media__unavailable"
          t="catalog.productSummary.soldOut"
        />
      )}
      {promotional_badge?.path && (
        <img
          width="auto"
          height="auto"
          loading="lazy"
          className="product-summary__media-promotional-badge"
          src={`${SF_URL}${promotional_badge.path}`}
          alt={promotional_badge.alt_text}
          srcSet={srcSetBadges?.length ? srcSetBadges.join() : null}
          sizes="(max-width: 767px) 112px,
          (min-width: 768px and max-width: 991px) 176px,
          (min-width: 992px) 192px"
        />
      )}
    </ProductSummaryMediaContainer>
  );
}
